import * as React from 'react';
import { MdBusiness, MdClose, MdGppBad, MdMoneyOff, MdPeople, MdRefresh, MdSearch, MdVisibility } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import DataGrid from '../../Components/Table/DataGrid';
import PageTitle from '../../Components/Common/PageTitle';
import SearchableDropdown from '../../Components/Subscription/SearchableDropdown';
import { APPRoutes } from '../../Utils/Route';
import { useAuth } from '../../Context/AuthContext';
import { PermissionCodes, permissionHas, resolveUserPermissionCodes } from '../../Utils/Permissions';
import { getDeniedClaims } from '../../Services/Claim360Service';
import ExportListButton from '../../Components/Claim360/ExportListButton';
import { formatUtcToLocalDate } from '../../Utils/DateTime';
import { formatDate, formatMoney } from '../../Utils/stediUtils';

const ALL = 'All';

// Dropdown options from the distinct values of one column, "All" first.
const optionsOf = (rows, key, allLabel) => [
  { value: ALL, label: allLabel },
  ...[...new Set(rows.map((row) => row[key]).filter((value) => value && value !== '-'))]
    .sort((a, b) => a.localeCompare(b))
    .map((value) => ({ value, label: value })),
];

export default function DenialManagementList() {
  const navigate = useNavigate();
  const { token, permissions, decodedToken } = useAuth();
  const userPermissionCodes = React.useMemo(
    () => resolveUserPermissionCodes({ permissions, decodedToken }),
    [permissions, decodedToken],
  );

  const canView = permissionHas(userPermissionCodes, PermissionCodes.CLAIM_STATUS_VIEW, decodedToken);
  const canRecheck = permissionHas(userPermissionCodes, PermissionCodes.CLAIM_STATUS_EDIT, decodedToken);

  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [search, setSearch] = React.useState('');
  const [payer, setPayer] = React.useState(ALL);
  const [organization, setOrganization] = React.useState(ALL);

  const loadDenials = React.useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const response = await getDeniedClaims(token);
      setRows((response?.data?.denials ?? []).map((denial) => ({
        ...denial,
        patient_name: denial.patientName || '-',
        payer_name: denial.payerName || denial.payerId || '-',
        organization_name: denial.organizationName || '-',
        payer_claim_number: denial.payerClaimNumber || '-',
        denial_reason: denial.denialReason || '-',
      })));
    } catch (error) {
      console.error('Failed to load denied claims:', error);
      toast.error(error.message || 'Unable to load denied claims.');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [token]);

  React.useEffect(() => {
    loadDenials();
  }, [loadDenials]);

  const filteredRows = React.useMemo(() => {
    const term = search.trim().toLowerCase();
    return rows.filter((row) => (
      (payer === ALL || row.payer_name === payer)
      && (organization === ALL || row.organization_name === organization)
      && (!term || [row.patient_name, row.memberId, row.payer_name, row.payer_claim_number, row.denial_reason, row.organization_name]
        .some((value) => String(value || '').toLowerCase().includes(term)))
    ));
  }, [rows, search, payer, organization]);

  const payerOptions = React.useMemo(() => optionsOf(rows, 'payer_name', 'All payers'), [rows]);
  const organizationOptions = React.useMemo(() => optionsOf(rows, 'organization_name', 'All organizations'), [rows]);

  // Cards follow the filters, so picking a payer shows that payer's denials.
  const stats = React.useMemo(() => {
    const billed = filteredRows.reduce((sum, row) => sum + (Number(row.billedAmount) || 0), 0);
    return [
      {
        label: 'Denied Claims',
        value: filteredRows.length,
        icon: <MdGppBad size={22} color="var(--hc-status-danger-text)" />,
        iconBg: 'var(--hc-status-danger-bg)',
        accentClass: 'border-danger',
        valueClass: 'text-danger',
      },
      {
        label: 'Denied Amount',
        value: formatMoney(billed),
        icon: <MdMoneyOff size={22} color="var(--hc-status-warning-text)" />,
        iconBg: 'var(--hc-status-warning-bg)',
        accentClass: 'border-warning',
        valueClass: 'text-warning',
      },
      {
        label: 'Patients Affected',
        value: new Set(filteredRows.map((row) => row.patientId).filter(Boolean)).size,
        icon: <MdPeople size={22} color="var(--hc-icon-blue-text)" />,
        iconBg: 'var(--hc-icon-blue-bg)',
        accentClass: 'border-primary',
        valueClass: 'text-primary',
      },
      {
        label: 'Payers',
        value: new Set(filteredRows.map((row) => row.payer_name).filter((value) => value !== '-')).size,
        icon: <MdBusiness size={22} color="var(--hc-status-success-text)" />,
        iconBg: 'var(--hc-icon-green-bg)',
        accentClass: 'border-success',
        valueClass: 'text-success',
      },
    ];
  }, [filteredRows]);

  const columns = React.useMemo(() => {
    const cols = [
      {
        id: 'patient_name',
        label: 'Patient',
        minWidth: 200,
        format: (value, row) => (
          <div>
            <div className="fw-semibold">{value}</div>
            {row.memberId && <div className="small text-secondary">Member {row.memberId}</div>}
          </div>
        ),
      },
      { id: 'payer_name', label: 'Payer', minWidth: 180 },
      { id: 'serviceDate', label: 'Service Date', minWidth: 130, format: (value) => (value ? formatDate(value) : '-') },
      { id: 'organization_name', label: 'Organization', minWidth: 170 },
      { id: 'checkedOn', label: 'Checked On', minWidth: 130, format: (value) => formatUtcToLocalDate(value) },
    ];

    if (canView || canRecheck) {
      cols.push({
        id: 'actions',
        label: 'Actions',
        minWidth: 110,
        align: 'center',
        format: (_value, row) => (
          <div className="d-flex align-items-center justify-content-center gap-2">
            {canView ? (
              <button
                type="button"
                title="View denied claim"
                className="btn btn-sm p-1 lh-1 text-primary border-0 rounded-2"
                style={{ backgroundColor: 'var(--hc-status-info-bg)' }}
                onClick={() => navigate(APPRoutes.DENIAL_MANAGEMENT_VIEW.replace(':id', row.checkId).replace(':claimIndex', row.claimIndex ?? 0))}
              >
                <MdVisibility size={16} />
              </button>
            ) : null}
            {canRecheck ? (
              <button
                type="button"
                title="Re-check claim status"
                className="btn btn-sm p-1 lh-1 hc-text-primary border-0 rounded-2"
                style={{ backgroundColor: 'var(--hc-icon-blue-bg)' }}
                onClick={() => navigate(APPRoutes.CLAIM_STATUS_EDIT.replace(':id', row.checkId))}
              >
                <MdRefresh size={16} />
              </button>
            ) : null}
          </div>
        ),
      });
    }
    return cols;
  }, [navigate, canView, canRecheck]);

  return (
    <>
      <PageTitle title="Denial Management - Aegis 360" />
      <div className="d-flex flex-md-row flex-column align-items-start justify-content-between gap-2 mb-4">
        <div>
          <h4 className="fw-bold mb-1 hc-text-primary fs-3">Denial Management</h4>
          <p className="text-secondary mb-0 small">
            Claims payers finalized as denied, from the latest claim status check of each claim.
          </p>
        </div>
        <div className="d-flex flex-wrap align-items-center gap-2">
          <ExportListButton kind="denials" rows={filteredRows} disabled={loading} />
          <button
            type="button"
            onClick={loadDenials}
            disabled={loading}
            className="btn hc-btn-prev btn-sm fw-semibold d-flex align-items-center justify-content-center gap-1 px-3 py-2 rounded-3 text-nowrap"
          >
            <MdRefresh size={18} />
            <span>Refresh</span>
          </button>
        </div>
      </div>

      <div className="row g-3 mb-3">
        {stats.map((stat) => (
          <div key={stat.label} className="col-12 col-sm-6 col-xl-3">
            {loading ? (
              <div className="bg-white p-4 rounded-4 shadow d-flex align-items-center gap-3 h-100">
                <div className="skeleton position-relative overflow-hidden rounded-2" style={{ width: 44, height: 44 }} />
                <div className="w-100">
                  <div className="skeleton mb-2" style={{ height: 16, width: '70%' }} />
                  <div className="skeleton position-relative overflow-hidden rounded-2" style={{ height: 24, width: '45%' }} />
                </div>
              </div>
            ) : (
              <div className="position-relative overflow-hidden bg-white p-4 rounded-4 shadow d-flex align-items-center gap-3 h-100 hc-list-kpi-card">
                <div className={`position-absolute start-0 top-0 h-100 border-start border-5 ${stat.accentClass}`} />
                <div
                  className="d-inline-flex align-items-center justify-content-center rounded-3 flex-shrink-0"
                  style={{ width: 44, height: 44, backgroundColor: stat.iconBg }}
                >
                  {stat.icon}
                </div>
                <div className="d-flex flex-column fw-bold lh-sm" style={{ minWidth: 0 }}>
                  <span className="text-dark" style={{ fontSize: '0.85rem' }}>{stat.label}</span>
                  <span className={`fw-bold fs-5 text-break ${stat.valueClass}`}>{stat.value}</span>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="bg-white rounded-3 shadow-lg p-3 p-md-4">
        <div className="row g-2 mb-3">
          <div className="col-12 col-lg-6">
            <div className="position-relative">
              <span
                className="position-absolute top-50 translate-middle-y d-inline-flex align-items-center justify-content-center text-secondary pe-none"
                style={{ left: '12px' }}
              >
                <MdSearch size={18} />
              </span>
              <input
                type="text"
                className="form-control form-control-sm hc-input"
                placeholder="Search patient, member ID or payer"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                style={{ paddingLeft: '36px', paddingRight: '36px', height: '40px' }}
              />
              {search ? (
                <button
                  type="button"
                  aria-label="Clear search"
                  onClick={() => setSearch('')}
                  className="btn btn-link bg-danger p-0 position-absolute top-50 translate-middle-y text-light rounded-circle"
                  style={{ right: '8px', lineHeight: 0 }}
                >
                  <MdClose size={18} />
                </button>
              ) : null}
            </div>
          </div>
          <div className="col-12 col-sm-6 col-lg-3">
            <SearchableDropdown
              placeholder="All payers"
              options={payerOptions}
              value={payer}
              onChange={(value) => setPayer(value || ALL)}
              showSearch
            />
          </div>
          <div className="col-12 col-sm-6 col-lg-3">
            <SearchableDropdown
              placeholder="All organizations"
              options={organizationOptions}
              value={organization}
              onChange={(value) => setOrganization(value || ALL)}
              showSearch
            />
          </div>
        </div>

        <div className="bg-white p-3 rounded shadow">
          {loading ? (
            <div>
              {[...Array(8)].map((_, i) => (
                <div key={i} className="d-flex gap-3 align-items-center mb-3">
                  <div className="skeleton position-relative overflow-hidden rounded-2 w-25" style={{ height: 16 }} />
                  <div className="skeleton position-relative overflow-hidden rounded-2" style={{ height: 16, width: '20%' }} />
                  <div className="skeleton position-relative overflow-hidden rounded-2 w-25" style={{ height: 16 }} />
                  <div className="skeleton position-relative overflow-hidden rounded-2" style={{ height: 16, width: '20%' }} />
                </div>
              ))}
            </div>
          ) : (
            <DataGrid
              columns={columns}
              rows={filteredRows}
              defaultRowsPerPage={10}
              stickyHeader
              getRowId={(row) => row.id}
            />
          )}
        </div>
      </div>
    </>
  );
}
